import { FieldSettingsModel } from '@syncfusion/ej2-angular-dropdowns'
import { StructureResults } from './credit-results.model'

export interface CreditMetricConfiguration {
  name: string
  carrierId: number
  weight: number // sum of weights of all metric settings
  metricSettings: CreditMetricSetting[]
}

export interface CreditMetricSetting {
  id?: number
  name: string // metric configuration name
  carrier_id: number // client id
  category: CreditMetricCategory
  metric: CreditMetric
  label: string
  type: CreditMetricValueType
  ordinal: number
  show: boolean // indicates if metric should be shown in compare module
  weight: number // indicates bubble size on chart in compare module
  formula?: string // to calculate custom metric
  scenario: string
  returnPeriod?: string
  mapping_scenario_id: number
  startdate?: string
  enddate?: string
  rag_order?: CreditMetricRagOrder // indicates if a higher or lower metric value equates to a better rank
}

export type CreditMetricCategorySettings = {
  ceded: CreditMetricCategorySetting
  net: CreditMetricCategorySetting
  custom: CreditMetricCategorySetting
}

export interface CreditMetricCategorySetting {
  configurationName: string
  carrierId: number
  category: CreditMetricCategory
  metricSettings: CreditMetricSetting[]
}

export type CreditMetricCategory = 'Ceded' | 'Net' | 'Custom'

export type CreditMetric = keyof StructureResults | 'custom'

export enum CreditMetricValueType {
  Currency = 'currency',
  Percentage = 'percentage',
  Numeric = 'numeric',
}
export enum CreditMetricRagOrder {
  HigherIsBetter = 'higher',
  LowerIsBetter = 'lower',
}

export const METRIC_VALUE_TYPE_OPTIONS: FieldSettingsModel[] = Object.values(
  CreditMetricValueType,
).map((type) => ({
  text: type.slice(0, 1).toUpperCase() + type.slice(1),
  value: type,
}))

export const METRIC_RAG_ORDER_OPTIONS: FieldSettingsModel[] = Object.values(
  CreditMetricRagOrder,
).map((ragOrder) => ({
  text: ragOrder.slice(0, 1).toUpperCase() + ragOrder.slice(1),
  value: ragOrder,
}))

export type CreditCustomMetricOperation =
  'add' | 'subtract' | 'multiply' | 'divide' | 'leftParen' | 'rightParen'

export interface CreditMetricOption {
  metric: CreditMetric
  label: string
  type: CreditMetricValueType
}

export interface CreditMetricCategoryOption {
  category: CreditMetricCategory
  metricOptions: CreditMetricOption[]
}

export const METRIC_OPTIONS: CreditMetricOption[] = [
  {
    metric: 'expected_loss',
    label: 'Expected Loss',
    type: CreditMetricValueType.Currency,
  },
  {
    metric: 'expected_premium',
    label: 'Expected Premium',
    type: CreditMetricValueType.Currency,
  },
  {
    metric: 'pmiers_capital',
    label: 'PMIERS Capital',
    type: CreditMetricValueType.Currency,
  },
  { metric: 'margin', label: 'Margin', type: CreditMetricValueType.Currency },
  {
    metric: 'annual_return_on_pmiers',
    label: 'Annual Return On PMIERS',
    type: CreditMetricValueType.Percentage,
  },
  {
    metric: 'annual_premium',
    label: 'Annual Premium',
    type: CreditMetricValueType.Percentage,
  },
  {
    metric: 'loss_ratio',
    label: 'Loss Ratio',
    type: CreditMetricValueType.Percentage,
  },
  {
    metric: 'rif',
    label: 'RIF',
    type: CreditMetricValueType.Currency,
  },
]

const METRIC_CATEGORY_OPTIONS: CreditMetricCategoryOption[] = [
  { category: 'Ceded', metricOptions: METRIC_OPTIONS },
  { category: 'Net', metricOptions: METRIC_OPTIONS },
]

export const METRIC_CATEGORY_OPTIONS_FLATTENED: FieldSettingsModel[] =
  METRIC_CATEGORY_OPTIONS.flatMap((cat) =>
    cat.metricOptions.map((metric) => ({
      text: `${cat.category}: ${metric.label}`,
      value: `${cat.category}:${metric.metric}`,
    })),
  )

export const GSE_METRIC_OPTIONS: CreditMetricOption[] = [
  { metric: 'expected_loss', label: 'Expected Loss', type: CreditMetricValueType.Currency },
  { metric: 'expected_premium', label: 'Expected Premium', type: CreditMetricValueType.Currency },
  { metric: 'pmiers_capital', label: 'ERCF Capital', type: CreditMetricValueType.Currency },
  { metric: 'margin', label: 'Margin', type: CreditMetricValueType.Currency },
  {
    metric: 'annual_return_on_pmiers',
    label: 'Annual Return On ERCF',
    type: CreditMetricValueType.Percentage,
  },
  { metric: 'annual_premium', label: 'Annual Premium', type: CreditMetricValueType.Percentage },
  { metric: 'loss_ratio', label: 'Loss Ratio', type: CreditMetricValueType.Percentage },
  { metric: 'combined_ratio', label: 'Combined Ratio', type: CreditMetricValueType.Percentage },
]

const GSE_METRIC_CATEGORY_OPTIONS: CreditMetricCategoryOption[] = [
  { category: 'Ceded', metricOptions: GSE_METRIC_OPTIONS },
  { category: 'Net', metricOptions: GSE_METRIC_OPTIONS },
]

export const GSE_METRIC_CATEGORY_OPTIONS_FLATTENED: FieldSettingsModel[] =
  GSE_METRIC_CATEGORY_OPTIONS.flatMap((cat) =>
    cat.metricOptions.map((metric) => ({
      text: `${cat.category}: ${metric.label}`,
      value: `${cat.category}:${metric.metric}`,
    })),
  )

export const DEFAULT_METRIC_CONFIGURATION_NAME = 'Unlabeled Metrics'

export const DEFAULT_METRIC_SETTINGS: CreditMetricSetting[] = [
  {
    category: 'Ceded',
    metric: 'expected_loss',
    label: 'Expected Loss - Milliman Baseline',
    type: CreditMetricValueType.Currency,
    ordinal: 0,
    show: true,
    weight: 8,
    formula: null,
    scenario: 'Milliman Baseline',
    returnPeriod: null,
    mapping_scenario_id: -1,
    carrier_id: -1,
    name: DEFAULT_METRIC_CONFIGURATION_NAME,
  },
  {
    category: 'Ceded',
    metric: 'expected_premium',
    label: 'Expected Premium - Milliman Baseline',
    type: CreditMetricValueType.Currency,
    ordinal: 1,
    show: false,
    weight: 7,
    formula: null,
    scenario: 'Milliman Baseline',
    returnPeriod: null,
    mapping_scenario_id: -1,
    carrier_id: -1,
    name: DEFAULT_METRIC_CONFIGURATION_NAME,
  },
  {
    category: 'Ceded',
    metric: 'pmiers_capital',
    label: 'PMIERS Capital - Milliman Baseline',
    type: CreditMetricValueType.Currency,
    ordinal: 2,
    show: false,
    weight: 7,
    formula: null,
    scenario: 'Milliman Baseline',
    returnPeriod: null,
    mapping_scenario_id: -1,
    carrier_id: -1,
    name: DEFAULT_METRIC_CONFIGURATION_NAME,
  },
  {
    category: 'Ceded',
    metric: 'expense',
    label: 'Expense - Milliman Baseline',
    type: CreditMetricValueType.Currency,
    ordinal: 3,
    show: false,
    weight: 7,
    formula: null,
    scenario: 'Milliman Baseline',
    returnPeriod: null,
    mapping_scenario_id: -1,
    carrier_id: -1,
    name: DEFAULT_METRIC_CONFIGURATION_NAME,
  },
  {
    category: 'Ceded',
    metric: 'margin',
    label: 'Margin - Milliman Baseline',
    type: CreditMetricValueType.Currency,
    ordinal: 4,
    show: false,
    weight: 7,
    formula: null,
    scenario: 'Milliman Baseline',
    returnPeriod: null,
    mapping_scenario_id: -1,
    carrier_id: -1,
    name: DEFAULT_METRIC_CONFIGURATION_NAME,
  },
  {
    category: 'Ceded',
    metric: 'annual_return_on_pmiers',
    label: 'Annual Return On PMIERS - Milliman Baseline',
    type: CreditMetricValueType.Percentage,
    ordinal: 5,
    show: false,
    weight: 7,
    formula: null,
    scenario: 'Milliman Baseline',
    returnPeriod: null,
    mapping_scenario_id: -1,
    carrier_id: -1,
    name: DEFAULT_METRIC_CONFIGURATION_NAME,
  },
  {
    category: 'Ceded',
    metric: 'loss_ratio',
    label: 'Loss Ratio - Milliman Baseline',
    type: CreditMetricValueType.Percentage,
    ordinal: 6,
    show: false,
    weight: 7,
    formula: null,
    scenario: 'Milliman Baseline',
    returnPeriod: null,
    mapping_scenario_id: -1,
    carrier_id: -1,
    name: DEFAULT_METRIC_CONFIGURATION_NAME,
  },
  {
    category: 'Ceded',
    metric: 'rif',
    label: 'RIF - Milliman Baseline',
    type: CreditMetricValueType.Currency,
    ordinal: 7,
    show: false,
    weight: 7,
    formula: null,
    scenario: 'Milliman Baseline',
    returnPeriod: null,
    mapping_scenario_id: -1,
    carrier_id: -1,
    name: DEFAULT_METRIC_CONFIGURATION_NAME,
  },
  {
    category: 'Net',
    metric: 'expected_loss',
    label: 'Expected Loss - Milliman Baseline',
    type: CreditMetricValueType.Currency,
    ordinal: 0,
    show: false,
    weight: 8,
    formula: null,
    scenario: 'Milliman Baseline',
    returnPeriod: null,
    mapping_scenario_id: -1,
    carrier_id: -1,
    name: DEFAULT_METRIC_CONFIGURATION_NAME,
  },
  {
    category: 'Net',
    metric: 'expected_premium',
    label: 'Expected Premium - Milliman Baseline',
    type: CreditMetricValueType.Currency,
    ordinal: 1,
    show: false,
    weight: 7,
    formula: null,
    scenario: 'Milliman Baseline',
    returnPeriod: null,
    mapping_scenario_id: -1,
    carrier_id: -1,
    name: DEFAULT_METRIC_CONFIGURATION_NAME,
  },
  {
    category: 'Net',
    metric: 'pmiers_capital',
    label: 'PMIERS Capital - Milliman Baseline',
    type: CreditMetricValueType.Currency,
    ordinal: 2,
    show: false,
    weight: 7,
    formula: null,
    scenario: 'Milliman Baseline',
    returnPeriod: null,
    mapping_scenario_id: -1,

    carrier_id: -1,
    name: DEFAULT_METRIC_CONFIGURATION_NAME,
  },
  {
    category: 'Net',
    metric: 'expense',
    label: 'Expense - Milliman Baseline',
    type: CreditMetricValueType.Currency,
    ordinal: 3,
    show: false,
    weight: 7,
    formula: null,
    scenario: 'Milliman Baseline',
    returnPeriod: null,
    mapping_scenario_id: -1,
    carrier_id: -1,
    name: DEFAULT_METRIC_CONFIGURATION_NAME,
  },
  {
    category: 'Net',
    metric: 'margin',
    label: 'Margin - Milliman Baseline',
    type: CreditMetricValueType.Currency,
    ordinal: 4,
    show: false,
    weight: 7,
    formula: null,
    scenario: 'Milliman Baseline',
    returnPeriod: null,
    mapping_scenario_id: -1,
    carrier_id: -1,
    name: DEFAULT_METRIC_CONFIGURATION_NAME,
  },
  {
    category: 'Net',
    metric: 'annual_return_on_pmiers',
    label: 'Annual Return On PMIERS - Milliman Baseline',
    type: CreditMetricValueType.Percentage,
    ordinal: 5,
    show: false,
    weight: 7,
    formula: null,
    scenario: 'Milliman Baseline',
    returnPeriod: null,
    mapping_scenario_id: -1,
    carrier_id: -1,
    name: DEFAULT_METRIC_CONFIGURATION_NAME,
  },
  {
    category: 'Net',
    metric: 'loss_ratio',
    label: 'Loss Ratio - Milliman Baseline',
    type: CreditMetricValueType.Percentage,
    ordinal: 6,
    show: false,
    weight: 7,
    formula: null,
    scenario: 'Milliman Baseline',
    returnPeriod: null,
    mapping_scenario_id: -1,
    carrier_id: -1,
    name: DEFAULT_METRIC_CONFIGURATION_NAME,
  },
  {
    category: 'Net',
    metric: 'rif',
    label: 'RIF - Milliman Baseline',
    type: CreditMetricValueType.Percentage,
    ordinal: 7,
    show: false,
    weight: 7,
    formula: null,
    scenario: 'Milliman Baseline',
    returnPeriod: null,
    mapping_scenario_id: -1,
    carrier_id: -1,
    name: DEFAULT_METRIC_CONFIGURATION_NAME,
  },
]

export const DEFAULT_GSE_METRIC_SETTINGS: CreditMetricSetting[] = [
  {
    category: 'Ceded',
    metric: 'expected_loss',
    label: 'Expected Loss - Milliman Baseline',
    type: CreditMetricValueType.Currency,
    ordinal: 0,
    show: true,
    weight: 8,
    formula: null,
    scenario: 'Milliman Baseline',
    returnPeriod: null,
    mapping_scenario_id: -1,
    carrier_id: -1,
    name: DEFAULT_METRIC_CONFIGURATION_NAME,
  },
  {
    category: 'Ceded',
    metric: 'expected_premium',
    label: 'Expected Premium - Milliman Baseline',
    type: CreditMetricValueType.Currency,
    ordinal: 1,
    show: false,
    weight: 7,
    formula: null,
    scenario: 'Milliman Baseline',
    returnPeriod: null,
    mapping_scenario_id: -1,
    carrier_id: -1,
    name: DEFAULT_METRIC_CONFIGURATION_NAME,
  },
  {
    category: 'Ceded',
    metric: 'pmiers_capital',
    label: 'ERCF Capital - Milliman Baseline',
    type: CreditMetricValueType.Currency,
    ordinal: 2,
    show: false,
    weight: 7,
    formula: null,
    scenario: 'Milliman Baseline',
    returnPeriod: null,
    mapping_scenario_id: -1,
    carrier_id: -1,
    name: DEFAULT_METRIC_CONFIGURATION_NAME,
  },
  {
    category: 'Ceded',
    metric: 'expense',
    label: 'Expense - Milliman Baseline',
    type: CreditMetricValueType.Currency,
    ordinal: 3,
    show: false,
    weight: 7,
    formula: null,
    scenario: 'Milliman Baseline',
    returnPeriod: null,
    mapping_scenario_id: -1,
    carrier_id: -1,
    name: DEFAULT_METRIC_CONFIGURATION_NAME,
  },
  {
    category: 'Ceded',
    metric: 'margin',
    label: 'Margin - Milliman Baseline',
    type: CreditMetricValueType.Currency,
    ordinal: 4,
    show: false,
    weight: 7,
    formula: null,
    scenario: 'Milliman Baseline',
    returnPeriod: null,
    mapping_scenario_id: -1,
    carrier_id: -1,
    name: DEFAULT_METRIC_CONFIGURATION_NAME,
  },
  {
    category: 'Ceded',
    metric: 'annual_return_on_pmiers',
    label: 'Annual Return On ERCF - Milliman Baseline',
    type: CreditMetricValueType.Percentage,
    ordinal: 5,
    show: false,
    weight: 7,
    formula: null,
    scenario: 'Milliman Baseline',
    returnPeriod: null,
    mapping_scenario_id: -1,
    carrier_id: -1,
    name: DEFAULT_METRIC_CONFIGURATION_NAME,
  },
  {
    category: 'Ceded',
    metric: 'loss_ratio',
    label: 'Loss Ratio - Milliman Baseline',
    type: CreditMetricValueType.Percentage,
    ordinal: 6,
    show: false,
    weight: 7,
    formula: null,
    scenario: 'Milliman Baseline',
    returnPeriod: null,
    mapping_scenario_id: -1,
    carrier_id: -1,
    name: DEFAULT_METRIC_CONFIGURATION_NAME,
  },
  {
    category: 'Ceded',
    metric: 'combined_ratio',
    label: 'Combined Ratio - Milliman Baseline',
    type: CreditMetricValueType.Percentage,
    ordinal: 7,
    show: false,
    weight: 7,
    formula: null,
    scenario: 'Milliman Baseline',
    returnPeriod: null,
    mapping_scenario_id: -1,
    carrier_id: -1,
    name: DEFAULT_METRIC_CONFIGURATION_NAME,
  },
  {
    category: 'Net',
    metric: 'expected_loss',
    label: 'Expected Loss - Milliman Baseline',
    type: CreditMetricValueType.Currency,
    ordinal: 0,
    show: false,
    weight: 8,
    formula: null,
    scenario: 'Milliman Baseline',
    returnPeriod: null,
    mapping_scenario_id: -1,
    carrier_id: -1,
    name: DEFAULT_METRIC_CONFIGURATION_NAME,
  },
  {
    category: 'Net',
    metric: 'expected_premium',
    label: 'Expected Premium - Milliman Baseline',
    type: CreditMetricValueType.Currency,
    ordinal: 1,
    show: false,
    weight: 7,
    formula: null,
    scenario: 'Milliman Baseline',
    returnPeriod: null,
    mapping_scenario_id: -1,
    carrier_id: -1,
    name: DEFAULT_METRIC_CONFIGURATION_NAME,
  },
  {
    category: 'Net',
    metric: 'pmiers_capital',
    label: 'ERCF Capital - Milliman Baseline',
    type: CreditMetricValueType.Currency,
    ordinal: 2,
    show: false,
    weight: 7,
    formula: null,
    scenario: 'Milliman Baseline',
    returnPeriod: null,
    mapping_scenario_id: -1,
    carrier_id: -1,
    name: DEFAULT_METRIC_CONFIGURATION_NAME,
  },
  {
    category: 'Net',
    metric: 'expense',
    label: 'Expense - Milliman Baseline',
    type: CreditMetricValueType.Currency,
    ordinal: 3,
    show: false,
    weight: 7,
    formula: null,
    scenario: 'Milliman Baseline',
    returnPeriod: null,
    mapping_scenario_id: -1,
    carrier_id: -1,
    name: DEFAULT_METRIC_CONFIGURATION_NAME,
  },
  {
    category: 'Net',
    metric: 'margin',
    label: 'Margin - Milliman Baseline',
    type: CreditMetricValueType.Currency,
    ordinal: 4,
    show: false,
    weight: 7,
    formula: null,
    scenario: 'Milliman Baseline',
    returnPeriod: null,
    mapping_scenario_id: -1,
    carrier_id: -1,
    name: DEFAULT_METRIC_CONFIGURATION_NAME,
  },
  {
    category: 'Net',
    metric: 'annual_return_on_pmiers',
    label: 'Annual Return On ERCF - Milliman Baseline',
    type: CreditMetricValueType.Percentage,
    ordinal: 5,
    show: false,
    weight: 7,
    formula: null,
    scenario: 'Milliman Baseline',
    returnPeriod: null,
    mapping_scenario_id: -1,
    carrier_id: -1,
    name: DEFAULT_METRIC_CONFIGURATION_NAME,
  },
  {
    category: 'Net',
    metric: 'loss_ratio',
    label: 'Loss Ratio - Milliman Baseline',
    type: CreditMetricValueType.Percentage,
    ordinal: 6,
    show: false,
    weight: 7,
    formula: null,
    scenario: 'Milliman Baseline',
    returnPeriod: null,
    mapping_scenario_id: -1,
    carrier_id: -1,
    name: DEFAULT_METRIC_CONFIGURATION_NAME,
  },
  {
    category: 'Net',
    metric: 'combined_ratio',
    label: 'Combined Ratio - Milliman Baseline',
    type: CreditMetricValueType.Percentage,
    ordinal: 7,
    show: false,
    weight: 7,
    formula: null,
    scenario: 'Milliman Baseline',
    returnPeriod: null,
    mapping_scenario_id: -1,
    carrier_id: -1,
    name: DEFAULT_METRIC_CONFIGURATION_NAME,
  },
]
